import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Heart, Users, MapPin, Globe, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { mockData } from '../mock';
import { scrollToSection } from '../utils/scrollToSection';

const Home = () => {
  const navigate = useNavigate();

  const handleScrollToSection = (path, sectionId) => {
    if (window.location.pathname === path) {
      scrollToSection(sectionId);
    } else {
      navigate(path);
      setTimeout(() => {
        if (path === '/donate') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          scrollToSection(sectionId);
        }
      }, 500);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="/images/Syrian Border Mission/syrian_border_1.jpg"
            // src="/images/Yammoune Mission/yammoune_1_cropped_2.jpg"
            alt="Medical mission providing care to communities"
            className="w-full h-full min-h-[100vh] object-cover blur-[1px] filter brightness-[50%]"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/50 to-black/60"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight drop-shadow-lg"
              style={{ fontFamily: 'Poppins, sans-serif' }}>

              {mockData.hero.tagline}
            </h1>
            <p
              className="text-lg md:text-xl text-white/95 mb-16 leading-relaxed"
              style={{ 
                fontFamily: 'Open Sans, sans-serif',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
              }}>

              {mockData.hero.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => handleScrollToSection('/donate', 'donation-form')}
                className="bg-red-500 hover:bg-red-600 text-white text-lg px-8 py-6 rounded-lg transition-all transform hover:scale-105 shadow-lg hover:shadow-xl">

                Donate Now
                <Heart className="ml-2 h-5 w-5 fill-white" />
              </Button>
              <Button
                onClick={() => handleScrollToSection('/get-involved', 'volunteer-form')}
                variant="outline"
                className="inline-flex items-center justify-center whitespace-nowrap focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-9 border-2 border-white bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all transform hover:scale-105 !font-medium !text-lg gap-2 px-8 py-6 rounded-lg shadow-sm text-white hover:text-white">

                Volunteer With Us
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Hero Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-20">
            {mockData.hero.stats.map((stat, index) =>
            <Card key={index} className="text-center border-none shadow-lg hover:shadow-xl transition-all bg-white/95 backdrop-blur-sm">
                <CardContent className="pt-8 pb-8">
                  <div className={`text-4xl md:text-5xl font-bold mb-2 ${
                    stat.label === 'Donations to Missions' 
                      ? 'text-green-600 flex items-center justify-center gap-2' 
                      : 'text-blue-600'
                  }`} style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {stat.label === 'Donations to Missions' && (
                      <Shield className="h-10 w-10 text-green-600 fill-green-100" />
                    )}
                    {stat.value}
                  </div>
                  <div className="text-gray-600 font-medium" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    {stat.label}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
              About Medics on Mission
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed" style={{ fontFamily: 'Open Sans, sans-serif' }}>
              {mockData.about.mission}
            </p>
          </div>
          <div className="text-center">
            <Button
              onClick={() => {
                navigate('/about');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-lg transition-all transform hover:scale-105">

              Learn More About Us
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section id="projects" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Our Missions
            </h2>
            <p className="text-lg text-gray-600" style={{ fontFamily: 'Open Sans, sans-serif' }}>
              Making a difference one community at a time
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {mockData.projects.slice(0, 2).map((project) => {
              return (
                <Card 
                  key={project.id} 
                  className="overflow-hidden hover:shadow-xl transition-shadow border-none cursor-pointer"
                  onClick={() => navigate(`/projects/${project.id}`)}
                >
                <div className="h-64 relative overflow-hidden">
                  <img 
                    src={project.thumbnail}
                    alt={project.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-8">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-semibold text-blue-600 uppercase tracking-wider" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {project.location}
                    </span>
                    <span className="text-sm text-gray-500" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                      {project.date}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {project.title}
                  </h3>
                  <p className="text-gray-600 mb-4 leading-relaxed" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    {project.description}
                  </p>
                  <div className="flex items-center text-sm font-semibold text-green-600">
                    <Users className="h-4 w-4 mr-2" />
                    {project.impact}
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>

          <div className="text-center">
            <Button
              onClick={() => {
                navigate('/projects');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              variant="outline"
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg rounded-lg transition-all transform hover:scale-105">

              View All Projects
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Our Impact
            </h2>
            <p className="text-xl text-blue-100" style={{ fontFamily: 'Open Sans, sans-serif' }}>
              Every donation makes a real difference
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {mockData.impactStats.map((stat, index) => {
              const IconComponent = stat.icon === 'Users' ? Users : stat.icon === 'MapPin' ? MapPin : stat.icon === 'Heart' ? Heart : Globe;
              return (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-4">
                    <IconComponent className="h-8 w-8 text-white" />
                  </div>
                  <div className="text-4xl md:text-5xl font-bold mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {stat.value}
                  </div>
                  <div className="text-blue-100 font-medium" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    {stat.label}
                  </div>
                </div>);

            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Join Us in Making a Difference
          </h2>
          <p className="text-xl text-gray-600 mb-12" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            Whether through donations, volunteering, or spreading the word, your support helps us bring healthcare to those who need it most.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => handleScrollToSection('/donate', 'donation-form')}
              className="bg-red-500 hover:bg-red-600 text-white text-lg px-10 py-6 rounded-lg transition-all transform hover:scale-105 shadow-lg">

              Make a Donation
            </Button>
            <Button
              onClick={() => handleScrollToSection('/get-involved', 'volunteer-form')}
              variant="outline"
              className="inline-flex items-center justify-center whitespace-nowrap focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:text-accent-foreground h-9 border-2 border-blue-600 hover:bg-blue-50 transition-all transform hover:scale-105 !font-medium !text-lg !pt-[24px] !pb-[24px] !pl-[32px] !pr-[32px] !gap-[8px] rounded-lg shadow-sm text-blue-600">

              Become a Volunteer
            </Button>
          </div>
        </div>
      </section>
    </div>);

};

export default Home;